import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import {
  Bell,
  ArrowLeft,
  Mail,
  Smartphone,
  MessageCircle,
  Users,
  Settings,
  Send,
  Key,
  Shield,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationSettings = ({ user }) => {
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState({
    email_notifications: true,
    push_notifications: true,
    email_on_new_message: true,
    email_on_new_match: true,
    push_on_new_message: true,
    push_on_new_match: true,
  });
  const [adminSettings, setAdminSettings] = useState({
    resend_api_key: "",
    sender_email: "notifications@localhomey.app",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);

  useEffect(() => {
    fetchPreferences();
    checkPushSupport();
    if (user?.role === "admin") {
      fetchAdminSettings();
    }
  }, [user]);

  const fetchPreferences = async () => {
    try {
      const response = await fetch(`${API}/notification-preferences`, {
        credentials: "include",
      });
      if (response.ok) {
        setPreferences(await response.json());
      }
    } catch (error) {
      console.error("Error fetching preferences:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminSettings = async () => {
    try {
      const response = await fetch(`${API}/admin/settings`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setAdminSettings({
          resend_api_key: data.resend_api_key || "",
          sender_email: data.sender_email || "notifications@localhomey.app",
        });
      }
    } catch (error) {
      console.error("Error fetching admin settings:", error);
    }
  };

  const checkPushSupport = () => {
    const supported = "Notification" in window && "serviceWorker" in navigator;
    setPushSupported(supported);
    if (supported && Notification.permission === "granted") {
      setPushSubscribed(true);
    }
  };

  const handlePreferenceChange = async (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);

    try {
      await fetch(`${API}/notification-preferences`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ [key]: value }),
      });
      toast.success("Preference updated");
    } catch (error) {
      toast.error("Failed to update preference");
      setPreferences(preferences); // Revert
    }
  };

  const handleRequestPushPermission = async () => {
    if (!pushSupported) {
      toast.error("Push notifications are not supported in this browser");
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        setPushSubscribed(true);
        toast.success("Push notifications enabled!");

        // Register service worker and subscribe
        if ("serviceWorker" in navigator) {
          const registration = await navigator.serviceWorker.ready;
          // Note: In production, you'd need VAPID keys configured
          toast.info("Push notifications are ready. Configure VAPID keys in admin settings for full functionality.");
        }
      } else {
        toast.error("Push notification permission denied");
      }
    } catch (error) {
      toast.error("Error requesting push permission");
    }
  };

  const handleSaveAdminSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(adminSettings),
      });

      if (response.ok) {
        toast.success("Settings saved!");
        fetchAdminSettings(); // Refresh to get masked key
      } else {
        toast.error("Failed to save settings");
      }
    } catch (error) {
      toast.error("Error saving settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    try {
      const response = await fetch(`${API}/admin/test-email`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send test email");
      }
    } catch (error) {
      toast.error("Error sending test email");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-12 h-12 border-4 border-[#4A6C6F] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#E2E8F0] px-4 lg:px-8 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/profile")}
              className="text-[#4A5568]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#4A6C6F] rounded-full flex items-center justify-center">
                <Bell className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-semibold text-[#1A202C]">
                Notification Settings
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 lg:px-8 py-8 space-y-6">
        {/* Email Notifications */}
        <Card className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-[#1A202C]">
              <div className="w-10 h-10 bg-[#4A6C6F]/10 rounded-xl flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#4A6C6F]" />
              </div>
              Email Notifications
            </CardTitle>
            <CardDescription>
              Receive important updates via email
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable Email Notifications</Label>
                <p className="text-sm text-[#718096]">Master toggle for all email notifications</p>
              </div>
              <Switch
                data-testid="toggle-email"
                checked={preferences.email_notifications}
                onCheckedChange={(v) => handlePreferenceChange("email_notifications", v)}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-[#718096]" />
                <div>
                  <Label>New Messages</Label>
                  <p className="text-sm text-[#718096]">Email when you receive a new message</p>
                </div>
              </div>
              <Switch
                data-testid="toggle-email-message"
                checked={preferences.email_on_new_message}
                onCheckedChange={(v) => handlePreferenceChange("email_on_new_message", v)}
                disabled={!preferences.email_notifications}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-[#718096]" />
                <div>
                  <Label>New Matches</Label>
                  <p className="text-sm text-[#718096]">Email when you get matched with someone</p>
                </div>
              </div>
              <Switch
                data-testid="toggle-email-match"
                checked={preferences.email_on_new_match}
                onCheckedChange={(v) => handlePreferenceChange("email_on_new_match", v)}
                disabled={!preferences.email_notifications}
              />
            </div>
          </CardContent>
        </Card>

        {/* Push Notifications */}
        <Card className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-[#1A202C]">
              <div className="w-10 h-10 bg-[#D97757]/10 rounded-xl flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-[#D97757]" />
              </div>
              Push Notifications
            </CardTitle>
            <CardDescription>
              Get instant notifications on your device
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!pushSupported ? (
              <div className="text-center py-4 text-[#718096]">
                <Smartphone className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Push notifications are not supported in this browser</p>
              </div>
            ) : (
              <>
                {!pushSubscribed ? (
                  <div className="text-center py-4">
                    <p className="text-[#4A5568] mb-4">
                      Enable push notifications to get instant updates
                    </p>
                    <Button
                      data-testid="enable-push-btn"
                      onClick={handleRequestPushPermission}
                      className="bg-[#4A6C6F] hover:bg-[#3A5658] text-white rounded-full"
                    >
                      <Bell className="w-4 h-4 mr-2" />
                      Enable Push Notifications
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Enable Push Notifications</Label>
                        <p className="text-sm text-[#718096]">Master toggle for all push notifications</p>
                      </div>
                      <Switch
                        data-testid="toggle-push"
                        checked={preferences.push_notifications}
                        onCheckedChange={(v) => handlePreferenceChange("push_notifications", v)}
                      />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <MessageCircle className="w-5 h-5 text-[#718096]" />
                        <div>
                          <Label>New Messages</Label>
                          <p className="text-sm text-[#718096]">Push when you receive a new message</p>
                        </div>
                      </div>
                      <Switch
                        data-testid="toggle-push-message"
                        checked={preferences.push_on_new_message}
                        onCheckedChange={(v) => handlePreferenceChange("push_on_new_message", v)}
                        disabled={!preferences.push_notifications}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Users className="w-5 h-5 text-[#718096]" />
                        <div>
                          <Label>New Matches</Label>
                          <p className="text-sm text-[#718096]">Push when you get matched with someone</p>
                        </div>
                      </div>
                      <Switch
                        data-testid="toggle-push-match"
                        checked={preferences.push_on_new_match}
                        onCheckedChange={(v) => handlePreferenceChange("push_on_new_match", v)}
                        disabled={!preferences.push_notifications}
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Admin Settings */}
        {user?.role === "admin" && (
          <Card className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-[#1A202C]">
                <div className="w-10 h-10 bg-[#ED8936]/10 rounded-xl flex items-center justify-center">
                  <Shield className="w-5 h-5 text-[#ED8936]" />
                </div>
                Admin: Email Configuration
              </CardTitle>
              <CardDescription>
                Configure Resend API for email notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="resend-key" className="flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Resend API Key
                </Label>
                <Input
                  id="resend-key"
                  data-testid="resend-api-key-input"
                  type="password"
                  placeholder="re_xxxxxxxxxxxxx"
                  value={adminSettings.resend_api_key}
                  onChange={(e) =>
                    setAdminSettings({ ...adminSettings, resend_api_key: e.target.value })
                  }
                />
                <p className="text-xs text-[#718096]">
                  Get your API key from{" "}
                  <a
                    href="https://resend.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#4A6C6F] hover:underline"
                  >
                    resend.com
                  </a>
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sender-email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Sender Email
                </Label>
                <Input
                  id="sender-email"
                  data-testid="sender-email-input"
                  type="email"
                  placeholder="notifications@yourdomain.com"
                  value={adminSettings.sender_email}
                  onChange={(e) =>
                    setAdminSettings({ ...adminSettings, sender_email: e.target.value })
                  }
                />
                <p className="text-xs text-[#718096]">
                  Must be a verified domain in Resend
                </p>
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  data-testid="save-settings-btn"
                  onClick={handleSaveAdminSettings}
                  disabled={saving}
                  className="bg-[#4A6C6F] hover:bg-[#3A5658] text-white rounded-full"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : "Save Settings"}
                </Button>
                <Button
                  data-testid="test-email-btn"
                  onClick={handleTestEmail}
                  variant="outline"
                  className="border-[#4A6C6F] text-[#4A6C6F] rounded-full"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Test Email
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default NotificationSettings;
